import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, ShieldCheck, UserCog, Phone, KeyRound, RotateCw } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Navbar } from "../components/Navbar";
import { api } from "../lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, lang, refreshAuth } = useApp();
  const navigate = useNavigate();
  const [role, setRole] = useState("patient"); // patient | admin | staff
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpId, setOtpId] = useState(null);
  const [mockCode, setMockCode] = useState(null);
  const [step, setStep] = useState("phone"); // phone | otp
  const [resendIn, setResendIn] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "staff") navigate("/staff", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  // Reset OTP state when switching role
  useEffect(() => {
    setStep("phone"); setPhone(""); setOtp(""); setOtpId(null); setMockCode(null); setResendIn(0);
  }, [role]);

  // Resend cooldown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    const digits = phone.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) {
      toast.error(lang === "hi" ? "10 अंकों का mobile number डालें" : "Enter a 10-digit mobile number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/auth/staff-otp-request", { phone: digits, role });
      setOtpId(r.data.otp_id);
      setMockCode(r.data.mock_code || null);
      setStep("otp");
      setResendIn(30);
      if (r.data.mock_code) {
        toast.success((lang === "hi" ? "OTP भेज दिया — Demo code: " : "OTP sent — Demo code: ") + r.data.mock_code);
      } else {
        toast.success(lang === "hi" ? `OTP भेज दिया गया ${r.data.phone_masked} पर` : `OTP sent to ${r.data.phone_masked}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (lang === "hi" ? "OTP भेज नहीं पाए" : "Failed to send OTP"));
    } finally {
      setSubmitting(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!otp || otp.length < 4) {
      toast.error(lang === "hi" ? "OTP डालें" : "Enter OTP");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/auth/staff-otp-verify", { otp_id: otpId, code: otp.trim() });
      const loggedRole = r.data?.role || r.data?.user?.role;
      toast.success(lang === "hi" ? "स्वागत है!" : "Welcome!");
      await refreshAuth();
      if (loggedRole === "admin") navigate("/admin", { replace: true });
      else if (loggedRole === "staff") navigate("/staff", { replace: true });
      else navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (lang === "hi" ? "OTP verify विफल" : "OTP verification failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const backToPhone = () => { setStep("phone"); setOtp(""); setOtpId(null); setMockCode(null); };

  const roles = [
    { key: "patient", icon: Stethoscope, title: lang === "hi" ? "मरीज़" : "Patient",
      color: "from-blue-600 to-emerald-500", testid: "role-tab-patient" },
    { key: "admin", icon: ShieldCheck, title: lang === "hi" ? "Admin" : "Admin",
      color: "from-amber-500 to-rose-500", testid: "role-tab-admin" },
    { key: "staff", icon: UserCog, title: lang === "hi" ? "Staff" : "Staff",
      color: "from-emerald-500 to-teal-600", testid: "role-tab-staff" },
  ];
  const currentRole = roles.find((r) => r.key === role);

  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="min-h-[85vh] grid lg:grid-cols-2">
        <div className={`hidden lg:flex bg-gradient-to-br ${currentRole.color} text-white p-12 flex-col justify-between transition-all duration-500`}>
          <div className="flex items-center gap-2 font-heading font-bold text-xl">
            <Stethoscope /> AASAN OPD
          </div>
          <div>
            <h2 className="font-heading text-4xl font-bold leading-tight">
              {lang === "hi" ? "अपना स्वास्थ्य, अपनी सुविधा पर।" : "Your health, on your terms."}
            </h2>
            <p className="mt-4 text-white/85">
              {lang === "hi"
                ? "एक ही जगह – अल्ट्रासाउंड, ओपीडी, एक्स-रे, सीटी, एमआरआई और फार्मेसी।"
                : "One place — Ultrasound, OPD, X-Ray, CT, MRI and Pharmacy."}
            </p>
          </div>
          <div className="text-white/70 text-sm">© AASAN OPD · Begusarai, Bihar</div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            <h1 className="font-heading text-3xl font-bold">
              {lang === "hi" ? "लॉगिन / रजिस्टर" : "Sign in / Register"}
            </h1>
            <p className="text-slate-600 mt-2 text-sm">
              {lang === "hi" ? "अपनी भूमिका चुनें" : "Choose your role"}
            </p>

            {/* Role tabs */}
            <div className="mt-6 grid grid-cols-3 gap-2 bg-slate-100 p-1 rounded-full" data-testid="login-role-tabs">
              {roles.map((r) => {
                const Icon = r.icon;
                const active = role === r.key;
                return (
                  <button key={r.key} data-testid={r.testid} onClick={() => setRole(r.key)}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                    }`}>
                    <Icon size={14} />
                    {r.title}
                  </button>
                );
              })}
            </div>

            {/* Patient (Google) */}
            {role === "patient" && (
              <div className="mt-8">
                <button onClick={handleGoogle} data-testid="login-google-btn"
                  className="w-full flex items-center justify-center gap-3 rounded-full bg-white border border-slate-200 text-slate-800 py-3 hover:border-blue-600 hover:text-blue-600 active:scale-95"
                  style={{ transition: "border-color .2s, color .2s" }}>
                  <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.6 2 30.2 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6c1.9-5.5 7-9.8 13.7-9.8z"/><path fill="#4285F4" d="M46.5 24.5c0-1.5-.2-3-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.7 7.2l7.2 5.6c4.2-3.9 6.8-9.7 6.8-17.3z"/><path fill="#FBBC05" d="M10.3 28.7c-.5-1.4-.8-2.9-.8-4.5s.3-3.1.8-4.5l-7.8-6C.9 17 0 20.4 0 24s.9 7 2.5 10.3l7.8-5.6z"/><path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.2-5.6c-2 1.4-4.6 2.3-8 2.3-6.7 0-12.3-4.3-14.3-10l-7.8 6C6.4 42.6 14.6 48 24 48z"/></svg>
                  {lang === "hi" ? "Google से जारी रखें" : "Continue with Google"}
                </button>
                <p className="text-xs text-slate-500 mt-6">
                  {lang === "hi"
                    ? "आगे बढ़ने पर आप हमारी शर्तों और गोपनीयता नीति से सहमत होते हैं।"
                    : "By continuing, you agree to our Terms and Privacy Policy."}
                </p>
              </div>
            )}

            {/* Admin / Staff — Mobile OTP */}
            {(role === "admin" || role === "staff") && step === "phone" && (
              <form onSubmit={sendOtp} className="mt-8 space-y-4" data-testid={`login-${role}-phone-form`}>
                <div>
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                    {lang === "hi" ? "मोबाइल नंबर" : "Mobile Number"}
                  </label>
                  <div className="mt-1 relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <span className="absolute left-9 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">+91</span>
                    <input type="tel" inputMode="numeric" maxLength={10} autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="98765 43210"
                      className="w-full rounded-xl bg-slate-50 border border-slate-200 pl-16 pr-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 tracking-wider"
                      data-testid={`login-${role}-phone-input`}/>
                  </div>
                </div>
                <button type="submit" disabled={submitting}
                  data-testid={`login-${role}-send-otp`}
                  className={`w-full rounded-full py-3 text-white font-medium bg-gradient-to-r ${currentRole.color} hover:opacity-95 active:scale-95 disabled:opacity-60`}>
                  {submitting
                    ? (lang === "hi" ? "OTP भेज रहा है…" : "Sending OTP…")
                    : (lang === "hi" ? "OTP भेजें" : "Send OTP")}
                </button>
                <p className="text-xs text-slate-500">
                  {role === "admin"
                    ? (lang === "hi" ? "सिर्फ registered admin mobile पर OTP भेजा जाएगा।" : "OTP will only be sent to a registered admin mobile.")
                    : (lang === "hi" ? "Staff mobile admin द्वारा add किया जाता है।" : "Staff numbers are added by the admin.")}
                </p>
              </form>
            )}

            {(role === "admin" || role === "staff") && step === "otp" && (
              <form onSubmit={verifyOtp} className="mt-8 space-y-4" data-testid={`login-${role}-otp-form`}>
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">{lang === "hi" ? "OTP भेजा गया" : "OTP sent to"}</span>
                    <span className="font-mono font-semibold">+91 {phone}</span>
                  </div>
                  <button type="button" onClick={backToPhone}
                    className="text-xs text-blue-600 hover:underline mt-1"
                    data-testid={`login-${role}-change-phone`}>
                    {lang === "hi" ? "नंबर बदलें" : "Change number"}
                  </button>
                  {mockCode && (
                    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                      <b>Demo mode:</b> code = <span className="font-mono">{mockCode}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                    {lang === "hi" ? "OTP डालें" : "Enter OTP"}
                  </label>
                  <div className="mt-1 relative">
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="6-digit code"
                      className="w-full rounded-xl bg-slate-50 border border-slate-200 pl-10 pr-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 tracking-[0.5em] text-center text-lg"
                      data-testid={`login-${role}-otp-input`}
                      autoFocus/>
                  </div>
                </div>

                <button type="submit" disabled={submitting}
                  data-testid={`login-${role}-verify-otp`}
                  className={`w-full rounded-full py-3 text-white font-medium bg-gradient-to-r ${currentRole.color} hover:opacity-95 active:scale-95 disabled:opacity-60`}>
                  {submitting
                    ? (lang === "hi" ? "Verify हो रहा है…" : "Verifying…")
                    : role === "admin"
                      ? (lang === "hi" ? "Admin लॉगिन" : "Sign in as Admin")
                      : (lang === "hi" ? "Staff लॉगिन" : "Sign in as Staff")}
                </button>

                <button type="button" onClick={sendOtp} disabled={resendIn > 0 || submitting}
                  data-testid={`login-${role}-resend-otp`}
                  className="w-full text-sm text-slate-500 hover:text-blue-600 inline-flex items-center justify-center gap-1 disabled:opacity-50">
                  <RotateCw size={14}/>
                  {resendIn > 0
                    ? (lang === "hi" ? `${resendIn}s में OTP दोबारा भेजें` : `Resend OTP in ${resendIn}s`)
                    : (lang === "hi" ? "OTP दोबारा भेजें" : "Resend OTP")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
